import { useMemo } from 'react';

import { FiatCodeEnum, FiatIdEnum } from '@shared/enums';
import { Big } from '@shared/safe-big';

import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { useFetchMarginAccountDetails } from './useFetchMarginAccountDetails';
import { getMarginRiskColor, getMarginRiskTitle } from '../../utils';

const useMarginAccount = () => {
  const { data, isFetching, isLoading: isLoadingDetails, isInitialLoading, isFetched } = useFetchMarginAccountDetails();
  const { getAssetByCode } = useMarkets();

  const currency = useMemo(() => {
    // Default to AUD if no account has been fetched yet

    if (!data?.account.currency) return getAssetByCode(FiatCodeEnum.AUD);

    return getAssetByCode(data.account.currency);
  }, [data?.account.currency, getAssetByCode]);

  const availableMargin = Big(data?.account?.freeMargin || 0).toNumber();
  const balanceAvailable = Big(data?.account?.balanceAvailable || 0).toNumber();
  const marginLevel = Big(data?.account?.marginLevel);
  const usedMargin = Big(data?.account?.margin);
  const profitLossTotal = Big(data?.account?.profitTotal);
  const equity = Big(data?.account?.equity);
  const commission = Big(data?.account?.commission).div(100);

  const riskColor = getMarginRiskColor(usedMargin, marginLevel);

  const riskRating = getMarginRiskTitle(usedMargin, marginLevel);

  const isLoading = useMemo(
    () => isLoadingDetails && isInitialLoading && !isFetched,
    [isFetched, isInitialLoading, isLoadingDetails],
  );

  return {
    account: data?.account,
    equity,
    riskColor,
    riskRating,
    availableMargin,
    balanceAvailable,
    currency,
    marginLevel,
    usedMargin,
    currencyId: currency?.id || FiatIdEnum.AUD,
    isFetching,
    isLoading,
    commission,
    profitLossTotal,
  };
};

export { useMarginAccount };
